import { ReactNode } from 'react';
import { Box, Card, CardContent, TableCell, TableRow, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { calendarDate, clientDay, dateTimeNoSeconds } from './dateTime';
import { MOCKUP_CLASS } from '../mockupLook/tokens';

// Wspólne elementy paneli szczegółów (jak SectionCard/Row w driver-documents).
export const SectionCard = ({
  title,
  children,
  sx,
}: {
  title: string;
  children: ReactNode;
  /** Np. `overflow: 'visible'` — karta MUI przycina treść, a przyklejony (`sticky`) element jej potrzebuje. */
  sx?: SxProps<Theme>;
}) => (
  <Card variant="outlined" sx={sx}>
    <CardContent sx={{ '&:last-child': { pb: 2 } }}>
      <Typography
        variant="overline"
        color="text.secondary"
        gutterBottom
        className={MOCKUP_CLASS.sectionTitle}
        sx={{ display: 'block' }}
      >
        {title}
      </Typography>
      {children}
    </CardContent>
  </Card>
);

/**
 * Wiersz „etykieta — wartość”. Kolumna etykiet ma szerokość najdłuższej
 * etykiety w karcie (`width: 1%` + `max-content`), ale nie więcej niż limit:
 * stałe 150 px łamało „Status w TMS klienta”, „Wymiary (dł × szer × wys)”
 * czy „Numer karty kierowcy” na dwie linie (28.09.2026). Powyżej limitu
 * etykieta się zawija — wartość obok zostaje szeroka; na telefonie limit
 * jest niższy, bo tam ważniejsza jest wartość.
 */
export const Row = ({ label, children }: { label: ReactNode; children: ReactNode }) => (
  <TableRow>
    <TableCell sx={{ width: '1%', color: 'text.secondary', border: 0, verticalAlign: 'top' }}>
      <Box sx={{ width: 'max-content', maxWidth: { xs: 130, md: 220 } }}>{label}</Box>
    </TableCell>
    <TableCell sx={{ border: 0, wordBreak: 'break-word' }}>{children ?? '—'}</TableCell>
  </TableRow>
);

/**
 * @deprecated Identyfikatory spółek należą do KONKRETNEGO klienta, więc nie
 * mogą siedzieć w kodzie — patrz `shared/companies.ts` i reguła „bez hardkodów
 * `companyId`" w CLAUDE.md. Zostaje jako cienka warstwa zgodności dla miejsc,
 * które nie mają jeszcze kontekstu react-admina: oddaje surowy numer, żeby
 * widać było, że rekord wskazuje spółkę, której nazwy nie znamy.
 */
export const companyLabel = (id?: string | null) => id ?? '';

/** „28.09.2026, 10:34" — bez sekund, czas polski; format siedzi w `shared/dateTime.ts`. */
export const fmtDate = (d?: string | null) => dateTimeNoSeconds(d);
/** „28.09.2026” — dzień w Polsce, w którym wypada CHWILA (okno zlecenia, ostatni kurs). */
export const fmtDay = (d?: string | null) => clientDay(d);
/**
 * „05.10.2026” — DATA bez godziny (termin ważności, ważność uprawnień,
 * nieobecność, pierwsza rejestracja): dzień z zapisu, bez strefy.
 */
export const fmtCalendarDate = (d?: string | null) => calendarDate(d);
